// src/components/ServerWakeBanner.jsx — friendly cold-start handling.
// The backend (Render) spins down when idle; the first request after that can
// take up to a minute. Previously requests hung and screens sat white/blank.
// Now: any no-response failure while online raises 'tt-server-cold' (see
// utils/api.js), this banner appears, pings /health until the server answers,
// then offers a one-tap refresh.
import { useState, useEffect, useRef } from 'react';
import { SERVER_ROOT } from '../utils/api';

export default function ServerWakeBanner() {
  const [state, setState] = useState('hidden'); // hidden | waking | ready
  const pollRef = useRef(null);

  useEffect(() => {
    const ping = async () => {
      try {
        const ctl = new AbortController();
        const t = setTimeout(() => ctl.abort(), 5000);
        const r = await fetch(`${SERVER_ROOT}/health`, { signal: ctl.signal, cache: 'no-store' });
        clearTimeout(t);
        return r.ok;
      } catch { return false; }
    };

    const onCold = async () => {
      if (pollRef.current) return; // already waking
      // Confirm it's really the server (not a one-off blip) before showing.
      if (await ping()) return;
      setState('waking');
      pollRef.current = setInterval(async () => {
        if (await ping()) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setState('ready');
        }
      }, 4000);
    };

    window.addEventListener('tt-server-cold', onCold);
    return () => { window.removeEventListener('tt-server-cold', onCold); clearInterval(pollRef.current); pollRef.current = null; };
  }, []);

  if (state === 'hidden') return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      padding: '9px 16px', fontSize: 12.5, fontWeight: 600, lineHeight: 1.4,
      background: state === 'waking' ? '#0c1428' : '#14532d', color: '#fff',
      boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
    }}>
      {state === 'waking' ? (
        <>
          <span style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          <span>Waking the server — first open can take up to a minute. Your cached data still works.</span>
        </>
      ) : (
        <>
          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12" /></svg>
          <span>Server is ready.</span>
          <button onClick={() => window.location.reload()}
            style={{ background: '#fff', color: '#14532d', border: 'none', borderRadius: 99, padding: '4px 14px', fontSize: 12, fontWeight: 800, cursor: 'pointer', flexShrink: 0 }}>
            Refresh
          </button>
        </>
      )}
    </div>
  );
}
